/**
 * URL minimization — the last gate before a URL leaves the box.
 *
 * Every Privacy Bee surface carries customer data in its query strings at some
 * point (`?email=` on a sign-in hand-off, `?token=` on a one-time link). The
 * ingestion platform also minimizes URLs, but private data must not cross this
 * boundary in the first place. What we never send cannot be retained, exported
 * or breached downstream.
 *
 * The key vocabulary starts from the ingestion platform's own strip list, so
 * both ends agree on what counts as sensitive, and extends it wherever a
 * Privacy Bee URL carries something the platform would not know to look for.
 * It is deliberately only ever a superset: stripping more than the platform
 * does is free, stripping less is a disclosure.
 */
/**
 * Substring tokens marking a query-param key as auth, session, PII or IP data.
 * Matched case-insensitively as substrings, so `oauth_token` and
 * `customer_email_address` are caught by `auth`/`token` and `email`.
 *
 * `name` covers the whole family in one token — `first_name`, `fullName`,
 * `username`, and the `assign_to_name` our own hive → checkout hand-off carries
 * a real person's name in. No attribution parameter contains the substring
 * (`utm_campaign` is the campaign's identifier and survives), so the broad
 * match costs nothing and closes the family for good rather than one spelling
 * at a time.
 */
const SENSITIVE_KEY_TOKENS = [
    'password', 'passwd', 'pwd', 'secret', 'token', 'jwt', 'bearer', 'auth', 'credential',
    'session', 'sessid', 'sessionid', 'phpsessid', 'jsessionid', 'sid', 'csrf', 'xsrf', 'nonce',
    'apikey', 'api_key', 'accesskey', 'access_key', 'privatekey', 'private_key',
    // SparkVault Forge names the single-use transfer key on an upload URL `istk`.
    // The browser streams to that URL directly, so the key rides resource timing,
    // fetch breadcrumbs and error reports without ever passing through our API.
    'istk',
    'email', 'e_mail', 'phone', 'name',
    'birthdate', 'passport', 'license', 'taxid', 'tax_id',
    'creditcard', 'credit_card', 'cardnumber', 'card_number', 'iban', 'routing',
    'accountnumber', 'account_number', 'address', 'street', 'postal', 'zipcode', 'zip_code',
    'ipaddr', 'ip_address', 'remoteaddr', 'remote_addr', 'x_forwarded',
];
/**
 * Exact-match sensitive keys — short names that would over-match as substrings.
 *
 * `code` and `state` are the OAuth pair our own sign-in hand-offs land on
 * (`/auth/callback?code=…&state=…`): an authorization code and the CSRF nonce
 * that guards it. Matched exactly, so the promo and discount codes that carry
 * real attribution (`promo`, `promo_code`, `coupon_code`) are untouched.
 */
const SENSITIVE_KEY_EXACT = new Set([
    'ip', 'ssn', 'dob', 'pan', 'cvv', 'cvc', 'zip', 'code', 'state',
]);
/**
 * Auth handoffs carry a full destination in redirect_url/redirect_uri;
 * checkout return_to carries a path and query. Either can nest personal data
 * or a capability token, so telemetry drops destinations whole. The anchored
 * key family covers camel, snake and hyphen spellings without inspecting or
 * recursively decoding values, or matching attribution such as utm_content.
 */
const DESTINATION_KEY = /^(?:redirect|return|callback|continue|next)(?:[_-]?(?:url|uri|to|path))?$/;
/** True when a query-param key holds auth, session, PII or IP data. */
export function isSensitiveTrackingKey(key) {
    const k = key.trim().toLowerCase();
    if (!k)
        return true;
    if (SENSITIVE_KEY_EXACT.has(k))
        return true;
    return SENSITIVE_KEY_TOKENS.some((token) => k.includes(token));
}
/**
 * The URL as it may be sent: sensitive and destination query params removed,
 * credentials and fragment dropped, attribution and non-capability paths kept.
 *
 * The fragment always goes: it never reaches a server, carries no attribution,
 * and is where OAuth implicit flows put access tokens.
 *
 * Returns null for a URL that cannot be parsed, so an unusable value fails
 * closed (nothing is sent) instead of being forwarded unexamined.
 */
export function sanitizeUrl(raw, options = {}) {
    let url;
    try {
        url = new URL(raw);
    }
    catch {
        return null;
    }
    url.username = '';
    url.password = '';
    url.hash = '';
    // Only rewrite the query when something must go: assigning through
    // URLSearchParams re-encodes every value, and an untouched query string
    // should reach the platform byte-identical to the one the visitor saw.
    const sensitive = [...url.searchParams.keys()].filter((key) => isSensitiveTrackingKey(key) || DESTINATION_KEY.test(key.trim().toLowerCase()));
    for (const key of sensitive) {
        url.searchParams.delete(key);
    }
    const redacted = redactPath(url.pathname, options.redactPathPrefixes);
    if (redacted !== url.pathname) {
        url.pathname = redacted;
    }
    return url.toString();
}
function redactPath(pathname, prefixes) {
    if (!prefixes?.length)
        return pathname;
    // Case-insensitive: the apps' routers resolve `/Consent/Minor/…` as the same
    // route, and the secret rides it just the same.
    const lower = pathname.toLowerCase();
    for (const prefix of prefixes) {
        const base = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
        // Only redact when there is actually a segment after the prefix — the
        // prefix on its own is a route, not a secret.
        if (lower.startsWith(`${base.toLowerCase()}/`) && lower.length > base.length + 1) {
            return `${base}/redacted`;
        }
    }
    return pathname;
}
